import { ApplicationRef, Component, OnInit, NgZone, Renderer2, OnDestroy } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Store } from '@ngrx/store';
import { concat, from, interval } from 'rxjs';
import { filter, first, take, map } from 'rxjs/operators';
import { NewServiceWorkerVersion } from './application-state/+state/application-state.actions';
import { ViewportService } from "./shared/viewport.service";
import { Platform } from '@ionic/angular';
import { environment } from '../environments/environment';
import { Actions, ofType } from '@ngrx/effects';
import { ErrorMessage, SaveStoreByAlias, SaveStoreByAliasFailed, SaveStoreByAliasSuccess, SetMobileStore, StoreActionType } from './public/store/+state/stores.actions';
import { StoreSaveMode } from './stores/stores';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { HelperService } from './public/helper.service';
import { APPMODE } from 'src/environments/appmode.enum';
import {WhitelabelService} from "./shared/whitelabel.service";
import { ClearIntegrationPayment, ClearOrderPayment } from './public/payments/+state/payment.actions';
import { LogService } from './shared/logging/LogService';
import { StoresService } from './stores/stores.service';
import { defaultPaging } from './api/types/Pageable';
import { OrderDirectPrint } from './printers/+state/printers.actions';
import { getNotificationSubscriptionsByStoreId } from './stores/+state/stores.selectors';
import { StatusBar } from '@awesome-cordova-plugins/status-bar';
import {App} from '@capacitor/app'
import { SignInWithAppleOptions, SignInWithAppleResponse, SignInWithApple } from '@capacitor-community/apple-sign-in';
import { MessageService } from './shared/message.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { Device, DeviceInfo } from '@capacitor/device';
import {
  FacebookLogin,
  FacebookLoginResponse,
} from '@capacitor-community/facebook-login';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { MobilePushNotificationService } from './shared/mobile-push-notification.service';
import { SplashScreen } from '@capacitor/splash-screen';
import { SafeArea } from 'capacitor-plugin-safe-area';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { AdminBarcodeScannerService } from './stores/store-events-bookings/stores-events-barcode-scanning-modal/admin-barcode-scanner.service';

export let browserRefresh = false;

interface QueryParams {
  [key: string]: string | number | boolean;
}

interface DeeplinkRoute {
  appEnvironment: string;
  fragment: string;
  path: string;
  queryParams: QueryParams;
  storeAlias: string;
  url: string;
}

@Component({
  // tslint:disable-next-line
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'body',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  jwtHelper = new JwtHelperService();
  title = 'GonnaOrder';
  isIOSApp: boolean;
  isGoAdminApp: boolean;
  loader: boolean = false;
  PushNotifications: any;
  private keyboardShowListener: Promise<PluginListenerHandle>;
  iosSafeAreaOverflowHeight: number = 0;
  private keyboardHideListener: Promise<PluginListenerHandle>;
  private resizeListener: () => void;

  constructor(private router: Router,
    private swUpdate: SwUpdate,
    private appRef: ApplicationRef,
    private store: Store<any>,
    private viewportService: ViewportService,
    private actions$: Actions,
    private ngZone: NgZone,
    private toastr: ToastrService,
    private translateSer: TranslateService,
    private platform: Platform,
    private helperService: HelperService,
    private whitelabelService: WhitelabelService,
    private logger: LogService,
    private storesService: StoresService,
    private renderer: Renderer2,
    private messageService: MessageService,
    private mobileNotificationService: MobilePushNotificationService,
    private adminBarcodeScannerService: AdminBarcodeScannerService
  ) {
    this.platform.ready().then(()=>{
      this.initializeApp();
    })
    this.isIOSApp = Capacitor.getPlatform() === 'ios';
    this.isGoAdminApp = helperService.isGoAdminApp();

    if (environment.mode !== APPMODE.Web) {
      this.platform.ready().then(async () => {
        if(Capacitor.getPlatform() === 'android'){
          await SplashScreen.show({
            showDuration: 3000,
            autoHide: true,
          });
        }
        this.initializeDeepLinks();

        if (this.helperService.isAndroid()) {
          StatusBar.overlaysWebView(false);
          StatusBar.backgroundColorByHexString('#000000');
        }

        if (helperService.isGoAdminApp()) {
          this.PushNotifications = this.mobileNotificationService.PushNotification;

          this.PushNotifications.addListener('pushNotificationReceived', (notification) => this.pushNotificationReceived(notification));
          this.PushNotifications.addListener('pushNotificationActionPerformed', (action) => this.pushNotificationActionPerformed(action));
        }
      });
    }

    router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        browserRefresh = !router.navigated;
      }
    });
  }

  private async initializeApp() {
    const deviceInfo = await Device.getInfo();
    if ((deviceInfo as unknown as DeviceInfo).platform !== "web"){
      try{
        GoogleAuth.initialize({
          clientId: environment.googleClientId,
          scopes: ['profile', 'email'],
          grantOfflineAccess: true,
          });
        } catch (error) {
          console.error("Error initializing Google Auth:", error);
        }
    } else {
      console.warn("Google Auth not initialized: Unsupported platform");
    }
  }
  private updateViewportHeight(): void {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }
  async ngOnInit() {
    this.resizeListener = () => this.updateViewportHeight();
    window.addEventListener('resize', this.resizeListener);

    if (this.helperService.isMobileApp() && this.whitelabelService.getEventManagementEnabled()) {
      await this.adminBarcodeScannerService.requestPermissions();
    }

    if (this.isIOSApp) {
      this.keyboardShowListener = Keyboard.addListener('keyboardDidShow', () => {
        setTimeout(() => {
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
          }
        }, 150);
      });

      this.keyboardHideListener = Keyboard.addListener('keyboardDidHide', () => {
        setTimeout(() => this.updateViewportHeight(), 50);
      });
    }

    //Message Service For Sign In
    this.messageService.receiveMessage().subscribe(async (message) => {
      if(message === 'appleSignIn'){
       this.appleSignIn();
      }else if(message === 'googleSignIn'){
        this.signInWithGoogle();
      }else if(message === 'facebookLogin'){
        this.facebookSignIn();
      }
    });
    console.log('checking for app version');
    if (this.swUpdate.isEnabled) {
      console.log('service worker enabled');

      const every30Seconds$ = interval(30 * 1000);
      const appIsStable$ = this.appRef.isStable.pipe(
        first(isStable => isStable === true)
      );

      concat(appIsStable$, every30Seconds$)
        .subscribe(() => {
          console.log('checking for updates');
          this.swUpdate.checkForUpdate();
        });

      this.swUpdate.versionUpdates.pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
        map(evt => {
          console.log('current version is', evt.currentVersion);
          console.log('available version is', evt.latestVersion);
          this.store.dispatch(new NewServiceWorkerVersion(evt.currentVersion.hash, evt.latestVersion.hash));
        })
      );

      this.swUpdate.activateUpdate().then(() => {
          console.log('update activated');
        });

      /*
      this.swUpdate.available.subscribe(event => {
        console.log('current version is', event.current);
        console.log('available version is', event.available);
        this.store.dispatch(new NewServiceWorkerVersion(event.current.hash, event.available.hash));
      });


      this.swUpdate.activated.subscribe(event => {
        console.log('old version was', event.previous);
        console.log('new version is', event.current);
      });
      */
    }

    this.router.events.subscribe((evt) => {
      if (!(evt instanceof NavigationEnd)) {
          return;
      }
      window.scrollTo(0, 0);
    });

    // disable zoom in for ios
    this.viewportService.applyCustomViewportForIOS();

    if (environment.mode == APPMODE.Web || environment.mode == APPMODE.Admin || environment.mode == APPMODE.Template || environment.mode == APPMODE.Customer || environment.mode == APPMODE.Kiosk) {
      this.title = this.whitelabelService.getWhiteLabelName();
      if(document.readyState !== 'loading') {
        this.setBrandColors();
      }
      else {
        document.addEventListener('DOMContentLoaded',  () => this.setBrandColors());
      }
    }

    if (this.isIOSApp) {
      SafeArea.getSafeAreaInsets().then(({ insets }) => {
        this.setSafeAreaInsets(insets);
      });

      SafeArea.addListener('safeAreaChanged', data => {
        const { insets } = data;
        this.setSafeAreaInsets(insets);
      });
    }
  }

  async ngOnDestroy() {
    if (this.keyboardShowListener) {
      (await (this.keyboardShowListener)).remove();
    }
    if (this.keyboardHideListener) {
      (await (this.keyboardHideListener)).remove();
    }
    if (this.resizeListener) window.removeEventListener('resize', this.resizeListener);
  }
  private setSafeAreaInsets(insets: any) {
    this.iosSafeAreaOverflowHeight = insets.top;

    for (const [key, value] of Object.entries(insets)) {
      document.documentElement.style.setProperty(
        `--safe-area-inset-${key}`,
        `${value}px`,
      );
    }
  }

  private async signInWithGoogle() {
    try {
      const result = await GoogleAuth.signIn();
      const user = {
        provider: 'google',
        accessToken: result.authentication.idToken,
        email: result.email,
        firstName: result.givenName,
        lastName: result.familyName,
        mode: this.helperService.isGoAdminApp() ? 'admin-login' : "customer-login",
      };
      window.postMessage(user, `${window.location.origin}/login`);
      // Handle user information, e.g., send to your backend or save locally
    } catch (error) {
      console.error('Error during Google sign-in:', error);
    }
  }
  private async appleSignIn(){
    let redirectURI = window.location.href;
    if (!redirectURI.startsWith('https://')) {
    console.error('Redirect URI is not HTTPS');
    // Handle non-HTTPS scenario
    } else {
      const uriParts = redirectURI.split('?');
      redirectURI = uriParts[0];
    }
    const rawNonce = 'nonce';
    const hashedNonceHex = await this.helperService.isSha256(rawNonce);
    let options: SignInWithAppleOptions = {
    clientId: environment.appleClientId,
    redirectURI,
    scopes: 'email name',
    nonce: hashedNonceHex,
  };
    try{
      const response: SignInWithAppleResponse = await SignInWithApple.authorize(options);
      if (response?.response?.identityToken && response?.response?.authorizationCode) {
        const appleToken = this.jwtHelper.decodeToken(response.response.identityToken);
        const socialAccountDetails = {
          provider: 'apple',
          accessToken: response.response.identityToken,
          appleCode: response.response.authorizationCode,
          email: appleToken.email,
          firstName: response.response.givenName,
          lastName: response.response.familyName,
          mode: this.helperService.isGoAdminApp() ? 'admin-login' : "customer-login"
        };
        window.postMessage(socialAccountDetails, `${window.location.origin}/login`);
      }
    } catch (err) {
      console.error('Sign in failed:', err);
      window.frames[0].postMessage({ status: 'error', error: err }, '*');
    }
  }
  private async facebookSignIn(){
    // Call the function to handle Facebook login
    let result:FacebookLoginResponse;
      this.handleFacebookLogin().then(async response => {
        result = response;
        if (result.accessToken.token!=undefined) {

        const FACEBOOK_PROFILE_FIELDS = [
          "email",
          "name",
          "picture",
          "first_name",
          "last_name"
        ];
        const user:any = await FacebookLogin.getProfile({ fields: FACEBOOK_PROFILE_FIELDS });

        if(user!=undefined){
          user.provider = 'FACEBOOK';
          user.authToken = result.accessToken.token;
          user.firstName = user.first_name;
          user.lastName = user.last_name;
          let mode = this.helperService.isGoAdminApp() ? 'admin-login' : "customer-login";
          window.postMessage({...user, mode: mode}, `${window.location.origin}/login`);
        }
      }
      }).catch(error => {
        console.error('Error during Facebook sign-in:', error);
      });
  }
  async handleFacebookLogin() {
    let result:FacebookLoginResponse;
    result = await this.loginWithFacebook();
    return result;
  }


  async loginWithFacebook() {
    const FACEBOOK_PERMISSIONS = [
      "email"
    ];
    const result = await FacebookLogin.login({ permissions: FACEBOOK_PERMISSIONS });
    return result;
  }

  private initializeDeepLinks(){
    App.addListener('appUrlOpen',(data)=>{
      const url = data.url;
      let route = this.extractRouteFromUrl(url);
      let routeType = this.extractRouteType(route);

      this.ngZone.run(()=>{
        switch (routeType) {
          case 'api':
            this.handleApiCall(route)
            break;
          case 'thankyou-redirect':
            this.handleThankyouRedirect(route);
            break;
          case 'stripe-verification':
            this.router.navigate([route.path],{queryParams:route.queryParams});
            break;
          case 'customer-app':
            this.handleCustomerApp(route,route.fragment);
            break;
          case 'template-app':
            this.handleTemplateApp(route);
            break;
          case 'payment-redirect':
            this.router.navigateByUrl(route.path);
            break;
          case 'standalone-payment':
            this.handleStandalonePayment(route);
            break;
          case 'payment-error':
            this.handleGeneralRedirect(route);
            break;
          default:
            console.error('No Route Matched', url);
        }
      })

    })
  }

  private extractRouteFromUrl(url: string) : DeeplinkRoute {
    let urlObj = new URL(url);
    const urlReplaced = urlObj.href.split(`${urlObj.protocol}//`)[1];
    urlObj = new URL(`https://${urlReplaced}`);
    const fragment = urlObj.hash.substring(1);
    const schemeSegments = url.split('://')[0].split('com.gonnaorder.');

    // Define recognized environments
    const recognizedEnvironments = ['go', 'goadmin', 'godev', 'goadmindev', 'kioskdev', 'kiosk'];

    // Detecting the environment or template based on the scheme
    let appEnvironment, storeAlias;

    if (recognizedEnvironments.some(env => schemeSegments[1]?.includes(env))) {
      appEnvironment = schemeSegments[1];  // Assuming the second part is the environment for customer apps
      const subdomainSegments = urlObj.hostname.split('.');
      storeAlias = subdomainSegments[0];  // Assuming the first segment is always the store alias for customer apps
    } else {
      // If no known environment, it's likely a template app, so the store alias is the second segment
      storeAlias = environment.templateStoreAlias;
      appEnvironment = 'template'; // Indicate that this is a template app environment
    }
    return {
        path: urlObj.pathname,
        queryParams: this.parseQueryParams(urlObj.search),
        fragment,
        storeAlias,
        appEnvironment,
        url: urlObj.href
    };
  }

  private parseQueryParams(search:string){
    const params = new URLSearchParams(search);
    const queryParams = {};
    params.forEach((value,key)=>{
      queryParams[key]=value;
    });
    return queryParams;
  }

  private extractRouteType(route:any){
    if(route.path.includes('/api')){
      return 'api';
    } else if(route.path.includes('/stripe/verification')){
      return 'stripe-verification';
    } else if(route.url.includes('thankyou')) {
      return 'thankyou-redirect';
    } else if (route.url.includes('paymentError')) {
      return 'payment-error';
    }else if (route.path.includes('/public/customer/store')){
      return 'customer-app';
    }else if(route.path.includes('/public')){
      return 'template-app';
    }else if(route.path.includes('payment-redirect')){
      return 'payment-redirect';
    } else if(route.path.includes('/manager')){
      return 'admin-app';
    } else if(route.path.includes('/standalone-payment')){
      return 'standalone-payment';
    }
    else {
      return 'unknown'; //Default Case if no type matches
    }
  }

  private handleApiCall(route: DeeplinkRoute) {
    this.storesService.checkCompletePaymentStatus(route.path).subscribe(
      (res: any) => {
        const url = new URL(res.redirectUrl);
        this.router.navigateByUrl(url.pathname)
      },
      err => {
        this.handleGeneralRedirect(route);
      }
    );
  }

  private handleThankyouRedirect(route: DeeplinkRoute) {
    if (this.helperService.isGoKioskApp()) {
      this.handleGeneralRedirect(route);
    } else if (this.helperService.isGoApp()) {
      this.handleCustomerApp(route, route.fragment);
    } else if (this.helperService.isGoTemplateApp()) {
      this.handleTemplateApp(route);
    } else if (this.helperService.isGoAdminApp()) {
      this.store.dispatch(new ClearIntegrationPayment({
        src: 'StoreCheckoutPaymentComponent',
        description: `Payment Status: INITIAL`
      }));
      this.router.navigateByUrl('/manager', { skipLocationChange: true }).then(() => {
        this.router.navigateByUrl(`${route.path}${route.fragment ? `#${route.fragment}` : ''}`);
      });
    }
  }

  private handleGeneralRedirect(route: DeeplinkRoute) {
    this.router.navigateByUrl('/', {skipLocationChange: false}).then(() => {
      setTimeout(() => {
        this.router.navigateByUrl(`${this.router.url}#${route.fragment}`);  
      }, 1000);
    });
  }

  private handleCustomerApp(route: DeeplinkRoute,fragment:string){
    const storeAlias = route.storeAlias;
    this.actions$.pipe(
      ofType<SaveStoreByAliasSuccess>(StoreActionType.SaveStoreByAliasSuccess),
      filter(action => action.saveMode === StoreSaveMode.DEEPLINK),
      take(1),
    ).subscribe(action => {
      if (action.storeAlias) {
        this.ngZone.run(() => {
          this.store.dispatch(new SetMobileStore(action.storeAlias));
          this.router.navigateByUrl('/public/customer', { skipLocationChange: true }).then(() => {
            this.router.navigateByUrl(`/public/customer/store/${action.storeAlias}${fragment ? `#${fragment}` : ''}`);
          })
        });
      }
    });
    this.actions$.pipe(
      ofType<SaveStoreByAliasFailed>(StoreActionType.SaveStoreByAliasFailed),
      take(1),
    ).subscribe(action => {
      this.ngZone.run(() => this.toastr.info(this.translateSer.instant('public.main.storeNotFound'), this.translateSer.instant('public.global.information')));
    })

    if (route.fragment.includes('thankyou') || route.fragment.includes('cart')) {
      this.store.dispatch(new ClearIntegrationPayment({
        src: 'StoreCheckoutPaymentComponent',
        description: `Payment Status: INITIAL`
      }));
    }

    this.store.dispatch(new SaveStoreByAlias(storeAlias, `https://${storeAlias}.${environment.envDomain}`, StoreSaveMode.DEEPLINK));
  }

  private handleTemplateApp(route: DeeplinkRoute){
    if (environment.templateStoreAlias && environment.templateStoreAlias == route.storeAlias) {
      this.ngZone.run(() => {
        this.store.dispatch(new ClearIntegrationPayment({
          src: 'StoreCheckoutPaymentComponent',
          description: `Payment Status: INITIAL`
        }));
        let baseUrl = '/'
        if (route.path.includes('/capture')) {
          baseUrl = '/manager'
        }
        this.router.navigateByUrl(baseUrl, { skipLocationChange: true }).then(() => {
          this.router.navigateByUrl(`${route.path}${route.fragment ? `#${route.fragment}` : ''}`);
        });
      });
    }
  }

  private handleStandalonePayment(route: DeeplinkRoute) {
    this.router.navigateByUrl('/', {skipLocationChange: false}).then(() => {
      this.router.navigateByUrl(route.path);
    });
  }

  private setBrandColors() {
    const primaryBrandColour = this.whitelabelService.getWhiteLabelPrimaryBrandColour();
    const secondaryBrandColour = this.whitelabelService.getWhiteLabelSecondaryBrandColour();
    // Set the custom properties for the current domain
    document.documentElement.style.setProperty('--primary-brand-color', primaryBrandColour, 'important');
    document.documentElement.style.setProperty('--secondary-brand-color', secondaryBrandColour, 'important');
    document.title = this.whitelabelService.getWhiteLabelName();
  }

  private pushNotificationReceived(notification) {
    let payload = notification?.data;

    if (payload) {
      this.storesService.list(defaultPaging, payload.storeAlias).pipe(
        take(1),
        filter(result => result.data.length > 0),
        map(result => result.data[0])
      ).subscribe(clientStore => {
        if (clientStore) {
          this.store.select(getNotificationSubscriptionsByStoreId, { storeId: clientStore.id }).pipe(take(1))
          .subscribe(notificationEnabled => {

            if (this.helperService.isAndroid() || this.helperService.isIOS()) {
              this.store.dispatch(new OrderDirectPrint(payload, clientStore.id));
            } else {
              this.logger.error('Not supported device');
              return;
            }
          });
        } else {

        }
      });
    } else {

    }
  }

  private pushNotificationActionPerformed(action) {
    let payload = action?.notification?.data;

    if (payload) {
      this.storesService.list(defaultPaging, payload.storeAlias).pipe(
        take(1),
        filter(result => result.data.length > 0),
        map(result => result.data[0])
      ).subscribe(clientStore => {
        if (clientStore) {
          if (this.helperService.isAndroid() || this.helperService.isIOS()) {
            if(action.actionId && action.actionId == "tap") {
              this.ngZone.run(() => {
                this.router.navigate([`/manager/stores/${clientStore.id}/orders`]).then(() => {
                  this.router.navigate([`/manager/stores/${clientStore.id}/orders/${payload.uuid}`])
                })
              });
            }
            this.store.dispatch(new OrderDirectPrint(payload, clientStore.id));
          } else {
            this.logger.error('Not supported device');
            return;
          }
        } else {

        }
      });
    } else {

    }
  }
}
